import { Stack } from '@mantine/core'
import { useForm, UseFormReturnType, zodResolver } from '@mantine/form'
import Form from 'common/Form'
import CommentsInput from 'common/inputs/CommentsInput'
import FeatureInput from 'common/inputs/FeatureInput'
import FinishInput from 'common/inputs/FinishInput'
import HeightInput from 'common/inputs/HeightInput'
import PillowSizeInput from 'common/inputs/PillowSizeInput'
import PlaceInput from 'common/inputs/PlaceInput'
import SizeInput from 'common/inputs/SizeInput'
import StockInput from 'common/inputs/StockInput'
import TableSizeInput from 'common/inputs/TableSizeInput'
import {
  Category,
  FabricVariantFields,
  PillowVariantFields,
  PremadeVariantFields,
  TableVariantFields,
  TowelVariantFields,
} from 'types/products'
import {
  fabricVariantSchema,
  pillowVariantSchema,
  premadeVariantSchema,
  tableVariantSchema,
  towelVariantSchema,
} from 'types/schemas'
import { trimFormValues } from 'utils'

type Props<T> = {
  onSubmit: (values: T) => void
  onCancel: () => void
  initialValues?: T
  actionType: 'add' | 'edit'
  disabled?: boolean
}

type FormInputsProps<T> = {
  form: UseFormReturnType<T>
  disabled?: boolean
}

export function FabricVariantForm({
  onSubmit,
  onCancel,
  initialValues,
  actionType,
  disabled,
}: Props<FabricVariantFields>) {
  const form = useForm<FabricVariantFields>({
    initialValues: initialValues,
    validate: zodResolver(fabricVariantSchema),
    transformValues: trimFormValues,
  })

  return (
    <Form onSubmit={onSubmit} form={form} onCancel={onCancel} submitText={getSubmitText(actionType)}>
      <FabricVariantFormInputs disabled={disabled} form={form} />
    </Form>
  )
}

export function FabricVariantFormInputs<Values extends FabricVariantFields>({
  form,
  disabled,
}: FormInputsProps<Values>) {
  return (
    <Stack gap="xs">
      <HeightInput {...form.getInputProps('size')} disabled={disabled} />
      <StockInput form={form} category={Category.FABRIC} disabled={disabled} />
      <PlaceInput form={form} disabled={disabled} />
      <FeatureInput form={form} disabled={disabled} />
      <CommentsInput {...form.getInputProps('comments')} disabled={disabled} />
    </Stack>
  )
}

export function PremadeVariantForm({
  onSubmit,
  onCancel,
  initialValues,
  actionType,
  disabled,
}: Props<PremadeVariantFields>) {
  const form = useForm<PremadeVariantFields>({
    initialValues: initialValues,
    validate: zodResolver(premadeVariantSchema),
    transformValues: trimFormValues,
  })

  return (
    <Form onSubmit={onSubmit} form={form} onCancel={onCancel} submitText={getSubmitText(actionType)}>
      <PremadeVariantFormInputs disabled={disabled} form={form} />
    </Form>
  )
}

export function PremadeVariantFormInputs<Values extends PremadeVariantFields>({
  form,
  disabled,
}: FormInputsProps<Values>) {
  return (
    <Stack gap="xs">
      <SizeInput
        disabled={disabled}
        heightProps={form.getInputProps('height')}
        widthProps={form.getInputProps('width')}
      />
      <StockInput disabled={disabled} form={form} category={Category.PREMADE} />
      <PlaceInput disabled={disabled} form={form} />
      <FinishInput disabled={disabled} form={form} />
      <CommentsInput disabled={disabled} {...form.getInputProps('comments')} />
    </Stack>
  )
}

export function PillowVariantForm({
  onSubmit,
  onCancel,
  initialValues,
  actionType,
  disabled,
}: Props<PillowVariantFields>) {
  const form = useForm<PillowVariantFields>({
    initialValues: initialValues,
    validate: zodResolver(pillowVariantSchema),
    transformValues: trimFormValues,
  })

  return (
    <Form onSubmit={onSubmit} form={form} onCancel={onCancel} submitText={getSubmitText(actionType)}>
      <PillowVariantFormInputs disabled={disabled} form={form} />
    </Form>
  )
}

export function PillowVariantFormInputs<Values extends PillowVariantFields>({
  form,
  disabled,
}: FormInputsProps<Values>) {
  return (
    <Stack gap="xs">
      <PillowSizeInput disabled={disabled} form={form} />
      <StockInput disabled={disabled} form={form} category={Category.PILLOW} />
      <PlaceInput disabled={disabled} form={form} />
      <FeatureInput form={form} disabled={disabled} />
      <CommentsInput disabled={disabled} {...form.getInputProps('comments')} />
    </Stack>
  )
}

export function TowelVariantForm({
  onSubmit,
  onCancel,
  initialValues,
  actionType,
  disabled,
}: Props<TowelVariantFields>) {
  const form = useForm<TowelVariantFields>({
    initialValues: initialValues,
    validate: zodResolver(towelVariantSchema),
    transformValues: trimFormValues,
  })

  return (
    <Form onSubmit={onSubmit} form={form} onCancel={onCancel} submitText={getSubmitText(actionType)}>
      <TowelVariantFormInputs disabled={disabled} form={form} />
    </Form>
  )
}

export function TowelVariantFormInputs<Values extends TowelVariantFields>({ form, disabled }: FormInputsProps<Values>) {
  return (
    <Stack gap="xs">
      <SizeInput
        disabled={disabled}
        heightProps={{
          ...form.getInputProps('length'),
          label: 'Długość',
          description: undefined,
          placeholder: '140 cm',
        }}
        widthProps={{ ...form.getInputProps('width'), description: undefined, placeholder: '70 cm' }}
      />
      <StockInput disabled={disabled} form={form} category={Category.TOWEL} />
      <PlaceInput disabled={disabled} form={form} />
      <FeatureInput form={form} disabled={disabled} />
      <CommentsInput disabled={disabled} {...form.getInputProps('comments')} />
    </Stack>
  )
}

export function TableVariantForm({
  onSubmit,
  onCancel,
  initialValues,
  actionType,
  disabled,
}: Props<TableVariantFields>) {
  const form = useForm<TableVariantFields>({
    initialValues: initialValues,
    validate: zodResolver(tableVariantSchema),
    transformValues: trimFormValues,
  })

  return (
    <Form onSubmit={onSubmit} form={form} onCancel={onCancel} submitText={getSubmitText(actionType)}>
      <TableVariantFormInputs disabled={disabled} form={form} />
    </Form>
  )
}

export function TableVariantFormInputs<Values extends TableVariantFields>({ form, disabled }: FormInputsProps<Values>) {
  return (
    <Stack gap="xs">
      <TableSizeInput disabled={disabled} form={form} />
      <StockInput disabled={disabled} form={form} category={Category.TABLE} />
      <PlaceInput disabled={disabled} form={form} />
      <FeatureInput form={form} disabled={disabled} />
      <CommentsInput disabled={disabled} {...form.getInputProps('comments')} />
    </Stack>
  )
}

function getSubmitText(actionType: 'add' | 'edit') {
  return actionType === 'add' ? 'Dodaj produkt' : 'Edytuj produkt'
}
